import { z } from 'zod';
import { IdSchema, InstantSchema, MoneySchema, type Money } from '../common/index.js';

/**
 * Ports des adaptateurs (architecture §1.5). Chaque adaptateur réel (lots L16 à L21) fournit aussi un factice,
 * et les deux passent la même suite de contrat (`./__contract__`). Le choix se fait par variable d'environnement.
 */

/* ---------- Messagerie (WhatsApp Jockalia, e-mail, SMS, courrier) ---------- */

export const StatutMessagePortSchema = z.enum(['en_file', 'envoye', 'delivre', 'lu', 'echec', 'inconnu']);
export type StatutMessagePort = z.infer<typeof StatutMessagePortSchema>;

export interface MessageAEnvoyer {
  to: string;
  text: string;
  /** Compte émetteur (numéro d'Isabelle ou de Brigitte, boîte du cabinet). */
  accountRef: string;
  /** En test, seuls les destinataires marqués fictifs sont acceptés (fixtures §7.1). */
  fictitious: boolean;
}

export interface MediaAEnvoyer extends MessageAEnvoyer {
  mediaUrl: string;
  mime: string;
}

export const AccuseEnvoiSchema = z.object({ externalId: z.string().min(1), status: z.enum(['en_file', 'envoye']) });
export type AccuseEnvoi = z.infer<typeof AccuseEnvoiSchema>;

export const MessageEntrantSchema = z.object({
  externalId: z.string().min(1),
  from: z.string().min(1),
  accountRef: z.string().min(1),
  text: z.string(),
  receivedAt: InstantSchema,
  media: z.array(z.object({ url: z.string().min(1), mime: z.string().min(3) })),
});
export type MessageEntrant = z.infer<typeof MessageEntrantSchema>;

export interface MessagingChannel {
  readonly canal: 'whatsapp' | 'email' | 'sms' | 'postal';
  send(message: MessageAEnvoyer, idempotencyKey: string): Promise<AccuseEnvoi>;
  sendMedia(message: MediaAEnvoyer, idempotencyKey: string): Promise<AccuseEnvoi>;
  getStatus(externalId: string): Promise<StatutMessagePort>;
  /** Renvoie null pour toute charge qui n'est pas un message entrant valide. */
  parseInbound(corps: unknown): MessageEntrant | null;
  verifySignature(corpsBrut: string, signature: string): boolean;
}

/* ---------- Signature électronique (DocuSeal) ---------- */

export const StatutSignatureSchema = z.enum(['envoye', 'ouvert', 'signe', 'refuse', 'expire', 'inconnu']);
export type StatutSignature = z.infer<typeof StatutSignatureSchema>;

export interface Signataire {
  personId: string;
  displayName: string;
  email: string;
}

export const SoumissionSchema = z.object({
  submissionId: z.string().min(1),
  signingUrls: z.array(z.object({ personId: IdSchema, url: z.string().min(1) })).min(1),
});
export type Soumission = z.infer<typeof SoumissionSchema>;

export const EvenementSignatureSchema = z.object({
  submissionId: z.string().min(1),
  status: StatutSignatureSchema,
  at: InstantSchema,
});
export type EvenementSignature = z.infer<typeof EvenementSignatureSchema>;

export interface SignatureProvider {
  createSubmission(modele: string, signataires: readonly Signataire[], idempotencyKey: string): Promise<Soumission>;
  getStatus(submissionId: string): Promise<StatutSignature>;
  /** null tant que le document n'est pas signé. */
  downloadSigned(submissionId: string): Promise<Uint8Array | null>;
  parseWebhook(corpsBrut: string, signature: string): EvenementSignature | null;
}

/* ---------- Paiements (Stripe, GoCardless, manuel) ---------- */

export interface DemandeLien {
  amount: Money;
  payerPersonId: string;
  label: string;
}

export const LienPaiementPortSchema = z.object({ linkId: z.string().min(1), url: z.string().min(1), expiresAt: InstantSchema });
export type LienPaiementPort = z.infer<typeof LienPaiementPortSchema>;

export interface DemandeEcheancier {
  total: Money;
  payerPersonId: string;
  count: number;
  firstDueAt: string;
}

export const EcheancierPortSchema = z.object({
  planId: z.string().min(1),
  installments: z.array(z.object({ dueAt: z.string().min(10), amount: MoneySchema })).min(1),
});
export type EcheancierPort = z.infer<typeof EcheancierPortSchema>;

export const RemboursementSchema = z.object({ refundId: z.string().min(1), amount: MoneySchema, status: z.enum(['accepte', 'refuse']) });
export type Remboursement = z.infer<typeof RemboursementSchema>;

export const EvenementPaiementSchema = z.object({
  paymentId: z.string().min(1),
  status: z.enum(['reussi', 'echoue', 'rembourse']),
  amount: MoneySchema,
  at: InstantSchema,
});
export type EvenementPaiement = z.infer<typeof EvenementPaiementSchema>;

export interface PaymentProvider {
  createPaymentLink(demande: DemandeLien, idempotencyKey: string): Promise<LienPaiementPort>;
  createMandate(payerPersonId: string, idempotencyKey: string): Promise<{ mandateId: string; url: string }>;
  createInstallmentPlan(demande: DemandeEcheancier, idempotencyKey: string): Promise<EcheancierPort>;
  /** Un remboursement supérieur au montant payé est refusé. */
  refund(paymentId: string, amount: Money): Promise<Remboursement>;
  parseWebhook(corpsBrut: string, signature: string): EvenementPaiement | null;
}

/* ---------- GED abstraite (S3 aujourd'hui, Odoo demain) ---------- */

export interface MetaDocument {
  dossierId: string;
  filename: string;
  mime: string;
}

export const ObjetStockeSchema = z.object({
  key: z.string().min(1),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  size: z.number().int().min(0),
});
export type ObjetStocke = z.infer<typeof ObjetStockeSchema>;

export interface DocumentStore {
  put(contenu: Uint8Array, meta: MetaDocument): Promise<ObjetStocke>;
  get(key: string): Promise<Uint8Array | null>;
  list(prefix: string): Promise<string[]>;
  versions(key: string): Promise<ObjetStocke[]>;
  /** Suppression logique : l'objet n'est plus lisible ni listé. */
  delete(key: string): Promise<void>;
  presign(key: string, ttlSeconds: number): Promise<string>;
}

/* ---------- IA : transcription, extraction, brouillons (toujours « à vérifier », RG-19) ---------- */

export const TranscriptionSchema = z.object({
  text: z.string(),
  segments: z.array(z.object({ startS: z.number().min(0), endS: z.number().min(0), text: z.string() })),
  lang: z.string().regex(/^[a-z]{2,3}$/),
});
export type Transcription = z.infer<typeof TranscriptionSchema>;

export interface Transcriber {
  transcribe(audio: Uint8Array, mime: string): Promise<Transcription>;
}

export const ExtractionFamilleSchema = z.object({
  persons: z.array(z.object({ ref: z.string().min(1), displayName: z.string().min(1), isHypothesis: z.boolean() })),
  links: z.array(z.object({ from: z.string().min(1), to: z.string().min(1), kind: z.enum(['parent', 'conjoint', 'fratrie']) })),
  confidence: z.number().min(0).max(1),
  status: z.literal('a_verifier'),
});
export type ExtractionFamille = z.infer<typeof ExtractionFamilleSchema>;

export const BrouillonSchema = z.object({ text: z.string().min(1), status: z.literal('a_verifier') });
export type Brouillon = z.infer<typeof BrouillonSchema>;

export interface Extractor {
  extractFamily(texte: string): Promise<ExtractionFamille>;
  draftMessage(contexte: Readonly<Record<string, string>>, modeleCode: string): Promise<Brouillon>;
}

/* ---------- Aperçu de lien (Open Graph générique) ---------- */

export const PAGES_ARRIVEE = ['accueil', 'piece', 'paiement', 'confidentialite', 'progression'] as const;
export type PageArrivee = (typeof PAGES_ARRIVEE)[number];

export const ApercuOgSchema = z.object({
  siteName: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  image: z.string().nullable(),
});
export type ApercuOg = z.infer<typeof ApercuOgSchema>;

export interface LinkPreview {
  ogFor(page: PageArrivee): ApercuOg;
}

/* ---------- Horloge et identifiants (indispensables aux tests des moteurs) ---------- */

export interface Clock {
  now(): string;
}

export interface IdGenerator {
  next(): string;
}
